import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Image, Settings } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

import { SectionCard } from "../../../../components/design-system/admin/SectionCard";
import { EmptyState } from "../../../../components/design-system/admin/EmptyState";
import { SpacesAsGrid } from "../../../vendor/my-spaces/components/spaces-as-grid.component";
import { VendorSpacesEditModal } from "./vendor-spaces-edit.modal";

interface VendorSpacesProps {
    vendor: any;
    setVendor: (vendor: any) => void;
    /** Show that at least one space is required (a new vendor after a failed save). */
    showRequired?: boolean;
}

export const VendorSpaces = ({ vendor, setVendor, showRequired }: VendorSpacesProps) => {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [maxSpacesTouched, setMaxSpacesTouched] = useState(false);

    const handleChange = (event) => {
        const { value } = event.target;
        setMaxSpacesTouched(true);
        // Convert to number for numeric fields
        setVendor({ ...vendor, maxSpaces: value === "" ? "" : parseInt(value, 10) });
    };

    const maxSpacesInvalid = maxSpacesTouched && !vendor.maxSpaces;
    const selectedCount = vendor.spaces?.filter((s) => !s.uploadedByVisitor).length || 0;

    const manageButton = (
        <Button variant="outline" size="sm" onClick={() => setIsModalOpen(true)}>
            <Settings className="size-4" />
            {t("ADMIN_VENDOREDIT.CATALOG.MANAGE_SPACES")}
        </Button>
    );

    return (
        <SectionCard
            title={t("ADMIN_VENDOREDIT.CATALOG.SPACES_TITLE")}
            description={t("ADMIN_VENDOREDIT.CATALOG.SPACES_DESCRIPTION")}
            action={manageButton}
            testId="vendor-spaces-card"
        >
            <div className="flex flex-col gap-5">
                <div className="md:max-w-xs">
                    <Label htmlFor="vendor-max-spaces" className="mb-1.5 text-xs font-medium text-muted-foreground">
                        {t("ADMIN_VENDOREDIT.CATALOG.MAX_SPACES")} *
                    </Label>
                    <Input
                        id="vendor-max-spaces"
                        aria-invalid={maxSpacesInvalid || undefined}
                        name="maxSpaces"
                        placeholder="3"
                        value={vendor?.maxSpaces ?? ""}
                        onChange={handleChange}
                        type="number"
                        min={1}
                        required
                    />
                    <p className="mt-1.5 text-xs text-muted-foreground">{t("ADMIN_VENDOREDIT.CATALOG.MAX_SPACES_HINT")}</p>
                </div>

                <div>
                    <p className="mb-3 text-sm font-medium">
                        {t("ADMIN_VENDOREDIT.CATALOG.SELECTED_SPACES", { count: selectedCount })}
                    </p>
                    {vendor.spaces && vendor.spaces.length > 0 ? (
                        <SpacesAsGrid
                            spaces={vendor.spaces}
                            maxSpaces={vendor?.maxSpaces && vendor.maxSpaces > 0 ? vendor.maxSpaces : 3}
                            selectedSpaces={[]}
                            setSelectedSpaces={() => {}}
                            filteredSpaces={vendor.spaces}
                            readOnly
                        />
                    ) : (
                        <EmptyState
                            icon={<Image />}
                            title={t("ADMIN_VENDOREDIT.CATALOG.NO_SPACES_TITLE")}
                            description={t("ADMIN_VENDOREDIT.CATALOG.NO_SPACES_BODY")}
                            action={manageButton}
                            className="rounded-lg border border-dashed"
                        />
                    )}
                    {showRequired && (!vendor.spaces || vendor.spaces.length === 0) && (
                        <p className="mt-2 text-xs text-destructive" role="alert">
                            {t("ADMIN_VENDOREDIT.CATALOG.SPACES_REQUIRED")}
                        </p>
                    )}
                </div>
            </div>

            {isModalOpen && (
                <VendorSpacesEditModal
                    vendor={vendor}
                    setVendor={setVendor}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                />
            )}
        </SectionCard>
    );
};
